/// <reference types="google.maps" />

import { Address } from "@/types";

import countries from "./countries.json";

const fieldMap: Record<string, string> = {
  "locality,political": "city",
  postal_code: "postalCode",
  street_number: "number",
  route: "street",
  subpremise: "address2",
  "administrative_area_level_1,political": "state",
  "country,political": "countryCode"
};

export type GoogleAddress = Pick<google.maps.places.PlaceResult, "address_components">;

export type AddressFields = Omit<Address, "id" | "addressType">;

export const hasLeadingStreetNumber = (value: string) => /^\s*\d/.test(value);
const usZipPattern = /\b\d{5}(?:-\d{4})?\b/;

export const firstAddressLineFromSuggestion = (suggestionLabel: string) => suggestionLabel.split(",")[0]?.trim() ?? "";
export const zipCodeFromSuggestion = (suggestionLabel: string) => suggestionLabel.match(usZipPattern)?.[0] ?? "";

export const code3FromCode2 = (code: string) => countries.find((c) => c.code2 === code)?.code3 || code;

const CANONICAL_COUNTRY_LABEL_BY_CODE3: Record<string, string> = {
  COD: "Democratic Republic of Congo",
  GBR: "United Kingdom",
  RUS: "Russian Federation"
};

export const countryCodeToLabel = (code: string) => {
  if (!code) return undefined;

  const code3 = code.length === 2 ? code3FromCode2(code) : code;
  const canonicalLabel = CANONICAL_COUNTRY_LABEL_BY_CODE3[code3];
  if (canonicalLabel) return canonicalLabel;

  return countries.find((c) => c.code3 === code3)?.label;
};

export function placeToFormData({ address_components: addressComponents = [] }: GoogleAddress): AddressFields {
  const fields = addressComponents.reduce<Omit<AddressFields, "address1"> & { number: string; street: string }>(
    (acc, { short_name, types }) => {
      const key = fieldMap[types.toString()];
      if (!key) return acc;
      return { ...acc, [key]: short_name };
    },
    { number: "", street: "", address2: "", city: "", state: "", postalCode: "", countryCode: "", isVerified: true }
  );

  const { street, number, postalCode, countryCode, ...rest } = fields;

  return {
    ...rest,
    countryCode: code3FromCode2(countryCode),
    postalCode: postalCode.substring(0, 5),
    address1: [number, street].filter(Boolean).join(" ").trim()
  };
}

export function applyAddress1SuggestionFallback(address: AddressFields, suggestionLabel: string): AddressFields {
  const parsedAddressLine1 = address.address1.trim();
  const suggestedAddressLine1 = firstAddressLineFromSuggestion(suggestionLabel);

  if (!suggestedAddressLine1) {
    return address;
  }

  if (!parsedAddressLine1) {
    return { ...address, address1: suggestedAddressLine1 };
  }

  if (hasLeadingStreetNumber(suggestedAddressLine1) && !hasLeadingStreetNumber(parsedAddressLine1)) {
    return { ...address, address1: suggestedAddressLine1 };
  }

  return address;
}

export function applyPostalCodeSuggestionFallback(address: AddressFields, suggestionLabel: string): AddressFields {
  if (address.postalCode.trim()) {
    return address;
  }

  const suggestionZip = zipCodeFromSuggestion(suggestionLabel);
  if (!suggestionZip) {
    return address;
  }

  return { ...address, postalCode: suggestionZip };
}
